<template>
  <v-container class="limited">
    <dados-beneficiario></dados-beneficiario>

    <v-card class="px-5 py-4 mb-4 elevation-5" v-if="nomePlano || contrato">
      <div class="caption text-uppercase grey--text">Plano contratado</div>
      <h3 class="title primary--text mt-1" v-if="nomePlano">
        {{ nomePlano }}
      </h3>
      <p class="body-1 grey--text text--darken-1 mt-1 mb-0" v-if="contrato">
        Contrato {{ contrato }}
      </p>
    </v-card>

    <v-card class="px-5 py-3 mb-4 elevation-5">
      <v-layout align-center justify-space-between>
        <h4 class="subheading font-weight-black primary--text my-3">
          Valores por Faixa Etária
        </h4>

        <v-btn
          color="primary"
          v-if="possuiFaixaBeneficiario"
          @click="mostraTodasFaixas = !mostraTodasFaixas"
        >
          {{
            mostraTodasFaixas
              ? "Ver apenas a minha faixa"
              : "Ver todas as faixas"
          }}
        </v-btn>
      </v-layout>

      <div class="rolagem-horizontal">
        <v-data-table
          class="elevation-0"
          rows-per-page-text="Linhas por página"
          :rows-per-page-items="pageOptions"
          :pagination.sync="paginacaoFaixasEtarias"
          :no-data-text="
            isLoading ? 'Carregando...' : 'Não há valores por faixa etária.'
          "
          :headers="headersFaixaEtaria"
          :items="faixasExibidas"
          :loading="isLoading"
        >
          <template v-slot:items="props">
            <tr>
              <td :class="classeFaixa(props.item)">
                {{ grauDependencia(props.item.grauDependencia) }}
                <v-chip
                  small
                  color="primary"
                  text-color="white"
                  v-if="props.item.faixaBeneficiario"
                >
                  Sua faixa
                </v-chip>
              </td>
              <td :class="classeFaixa(props.item)">
                {{ faixaEtaria(props.item) }}
              </td>
              <td class="text-xs-right" :class="classeFaixa(props.item)">
                {{ props.item.valorMensalidade | currency }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>

      <p class="caption mt-3 mb-0">
        {{ legendaFaixas }} Valores da tabela de preço vigente do contrato.
      </p>
    </v-card>

    <v-card class="px-5 py-3 mb-4 elevation-5">
      <h4 class="subheading font-weight-black primary--text my-3">
        Regras de Coparticipação
      </h4>

      <div class="rolagem-horizontal">
        <v-data-table
          class="elevation-0"
          rows-per-page-text="Linhas por página"
          :rows-per-page-items="pageOptions"
          :pagination.sync="paginacaoCoparticipacoes"
          :no-data-text="
            isLoading ? 'Carregando...' : 'Não há regras de coparticipação.'
          "
          :headers="headersCoparticipacao"
          :items="coparticipacoes"
          :loading="isLoading"
        >
          <template v-slot:items="props">
            <td>
              {{ props.item.descricaoGrupo || props.item.grupoCoparticipacao }}
            </td>
            <td>{{ props.item.tipo }}</td>
            <td class="text-xs-right">{{ percentual(props.item.eletiva) }}</td>
            <td class="text-xs-right">{{ percentual(props.item.urgencia) }}</td>
            <td class="text-xs-right">{{ valor(props.item.valorFixo) }}</td>
          </template>
        </v-data-table>
      </div>

      <p class="caption mt-3 mb-0">
        Regras de coparticipação vigentes no contrato atual.
      </p>
    </v-card>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from "vuex";

import DadosBeneficiario from "@/components/BeneficiaryData";
import { getValoresPlano } from "@/services/beneficiario";

const beneficiarioStore = createNamespacedHelpers("beneficiario");
const portalStore = createNamespacedHelpers("portal");

const IDADE_ULTIMA_FAIXA = 99;

export default {
  name: "ValoresPlano",
  components: {
    DadosBeneficiario
  },
  data() {
    return {
      isLoading: false,
      contrato: null,
      nomePlano: null,
      mostraTodasFaixas: false,
      faixasEtarias: [],
      coparticipacoes: [],
      pageOptions: [5, 10, 25, { value: -1, text: "Todos" }],
      paginacaoFaixasEtarias: { rowsPerPage: 10 },
      paginacaoCoparticipacoes: { rowsPerPage: 10 },
      headersFaixaEtaria: [
        {
          text: "Grau de dependência",
          value: "grauDependencia",
          sortable: false
        },
        { text: "Faixa etária", value: "idadeFaixaInicial", sortable: false },
        {
          text: "Valor",
          value: "valorMensalidade",
          sortable: false,
          align: "right"
        }
      ],
      headersCoparticipacao: [
        { text: "Grupo", value: "descricaoGrupo", sortable: false },
        { text: "Tipo de cobrança", value: "tipo", sortable: false },
        { text: "Eletiva", value: "eletiva", sortable: false, align: "right" },
        {
          text: "Urgência",
          value: "urgencia",
          sortable: false,
          align: "right"
        },
        {
          text: "Valor fixo",
          value: "valorFixo",
          sortable: false,
          align: "right"
        }
      ]
    };
  },
  computed: {
    ...beneficiarioStore.mapState(["dadosBeneficiario"]),
    faixasBeneficiario() {
      return this.faixasEtarias.filter(faixa => faixa.faixaBeneficiario);
    },
    possuiFaixaBeneficiario() {
      return this.faixasBeneficiario.length > 0;
    },
    faixasExibidas() {
      return this.mostraTodasFaixas || !this.possuiFaixaBeneficiario
        ? this.faixasEtarias
        : this.faixasBeneficiario;
    },
    legendaFaixas() {
      if (!this.possuiFaixaBeneficiario) {
        return "";
      }

      return this.mostraTodasFaixas
        ? "As faixas do beneficiário estão destacadas na tabela."
        : "Mostrando apenas as faixas aplicáveis ao beneficiário.";
    }
  },
  watch: {
    "dadosBeneficiario.id": {
      immediate: true,
      handler() {
        this.carregaValoresPlano();
      }
    }
  },
  methods: {
    ...portalStore.mapMutations(["setToast"]),
    carregaValoresPlano() {
      const codigoBeneficiario = this.dadosBeneficiario?.id;

      if (!codigoBeneficiario) {
        return;
      }

      this.isLoading = true;

      getValoresPlano(codigoBeneficiario)
        .then(valoresPlano => {
          this.contrato = valoresPlano?.contrato || null;
          this.nomePlano = valoresPlano?.nomePlano || null;
          this.faixasEtarias = valoresPlano?.faixasEtarias || [];
          this.coparticipacoes = valoresPlano?.coparticipacoes || [];
        })
        .catch(() => {
          this.setToast("Não foi possível carregar os valores do seu plano.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    classeFaixa(faixa) {
      return { "faixa-beneficiario": faixa.faixaBeneficiario };
    },
    grauDependencia(grau) {
      return grau ? grau.replace(/^\d+\s*-\s*/, "") : "";
    },
    faixaEtaria({ idadeFaixaInicial, idadeFaixaFinal }) {
      if (idadeFaixaInicial == null) {
        return "";
      }

      if (idadeFaixaFinal != null && idadeFaixaFinal < IDADE_ULTIMA_FAIXA) {
        return `${idadeFaixaInicial} a ${idadeFaixaFinal} anos`;
      }

      return idadeFaixaInicial > 0
        ? `${idadeFaixaInicial} anos ou mais`
        : "Todas as idades";
    },
    percentual(percentualCoparticipacao) {
      return percentualCoparticipacao != null
        ? `${percentualCoparticipacao}%`
        : "-";
    },
    valor(valorFixo) {
      return valorFixo != null
        ? this.$options.filters.currency(valorFixo)
        : "-";
    }
  }
};
</script>

<style scoped lang="scss">
.rolagem-horizontal {
  overflow-x: auto;
}

.faixa-beneficiario {
  background-color: #f0f0f0 !important;
}
</style>
